import { Injectable } from '@angular/core';
import FileSaver from 'file-saver';
import { Observable, Subscription } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';

import { LoadingDialogUtilService } from 'v2/modules/shared/services/util/loading-dialog-util.service';

import { ClientStateService } from './../../client/client-state.service';
import {
  IBusinessAccountContact,
  ICompany,
} from './../../client/interfaces/client-company.interface';
import { IClientShipping } from './../../client/interfaces/client-shipping.interface';
import { IPrice } from './../../pricing/interfaces/pricing.interface';
import { IPaginationResponse } from './../../shared/interfaces/paginator.interface';
import { ClientCompanyApiService } from './../../shared/services/api/client/client-company-api.service';
import { ClientShippingApiService } from './../../shared/services/api/client/client-shipping-api.service';
import { OrderPostedApiService } from './../../shared/services/api/order/order-posted-api.service';
import { PricingApiService } from './../../shared/services/api/pricing/pricing-api.service';
import { SalesOrderApiService } from './../../shared/services/api/sales-order-api.service';
import { SalesOrderStateService } from './sales-order-state.service';
@Injectable({
  providedIn: 'root',
})
export class SalesOrderFacadeService {
  constructor(
    private clientStateService :ClientStateService,
    private clientCompanyApiService :ClientCompanyApiService,
    private clientShippingApiService :ClientShippingApiService,
    private pricingApiService :PricingApiService,
    private salesOrderApiService :SalesOrderApiService,
    private salesOrderStateService :SalesOrderStateService,
    private orderPostedApiService :OrderPostedApiService,
    private loadingDialogService :LoadingDialogUtilService,
  ) {}

  getCompanyContacts(params :{
    [param :string] :string | string[];
  }) :Observable<IPaginationResponse> {
    return this.clientCompanyApiService.getCompanyContactsV2(params).pipe(
      tap((response :IPaginationResponse) => {
        const { data } = response;
        this.clientStateService.updateState({ businessAccountsContacts: data });
      }),
    );
  }

  getById(data :{
    id :number;
    params ?:{ [param :string] :string | string[] };
  }) :Observable<any> {
    return this.orderPostedApiService.getById(data).pipe(
      tap((response) => {
        this.salesOrderStateService.updateState({ currentSalesOrder: response });
        this.clientStateService.updateState({
          currentCompany: response.businessAccount,
        });
      }),
    );
  }

  getCompanyShippingAddresses({
    businessAccountId,
    params,
  }) :Observable<IClientShipping[]> {
    return this.clientShippingApiService.getV2({ businessAccountId, params }).pipe(
      tap((shippingAddresses :IClientShipping[]) => {
        this.clientStateService.updateState({
          businessAccountShippingAddresses: shippingAddresses,
        });
      }),
    );
  }

  getByRfq(params ?:{ [param :string] :string }) :Subscription {
    return this.pricingApiService.getByRfq(params);
  }

  setCurrentCompany(company :ICompany | null) :void {
    this.clientStateService.updateState({ currentCompany: company });

    if (company) {
      this.getCompanyContacts({
        businessAccountId: company?.id.toString(),
        limit: '1000',
      }).subscribe();
      this.getCompanyShippingAddresses({
        businessAccountId: company?.id ?? null,
        params: { allAddresses: 'true' },
      }).subscribe();
    }
  }

  resetCompanyState() :void {
    this.clientStateService.updateState({ currentCompany: null });
    this.clientStateService.updateState({ businessAccountsContacts: [] });
    this.clientStateService.updateState({ businessAccountShippingAddresses: [] });
  }

  createSalesOrder(payload :any) :Observable<any> {
    return this.salesOrderApiService.createOrder(payload).pipe(
      tap((response) => {
        this.resetCompanyState();
      }),
    );
  }

  updateSalesOrder(payload :any, salesOrderId :number) :Observable<any> {
    return this.salesOrderApiService.updateOrder(payload, salesOrderId).pipe(
      tap((response) => {
        this.resetCompanyState();
      }),
    );
  }

  updateState(newState :any) :void {
    this.salesOrderStateService.updateState(newState);
  }

  exportBulkSalesOrder(salesOrderIds :number[], exportType :string) {
    const params = {
      exportType: exportType,
      salesOrderIds: salesOrderIds.join(','),
    };
    this.loadingDialogService.show('Creating Excel, please wait...');
    this.salesOrderApiService.exportBulkSalesOrder(params).subscribe({
      next: (resp :any) => {
        FileSaver.saveAs(resp, 'Sales Orders.xlsx');
        this.loadingDialogService.close();
      },
      error: () => {
        this.loadingDialogService.close();
        this.loadingDialogService.showDialogMessage('Error Export');
      },
    });
  }

  exportFulfillmentAllocationSheet(salesOrderIds :number[]) {
    this.loadingDialogService.show('Creating Fulfillment Allocation Sheet, please wait...');
    this.salesOrderApiService.exportFulfillmentAllocationSheet(salesOrderIds).subscribe({
      next: (resp :any) => {
        FileSaver.saveAs(resp, 'Fulfillment Allocation Sheet.xlsx');
        this.loadingDialogService.close();
      },
      error: () => {
        this.loadingDialogService.close();
        this.loadingDialogService.showDialogMessage('Error Export');
      },
    });
  }

  getNextOrderNumber(params ?:{ [param :string] :string }) :Observable<any> {
    return this.salesOrderApiService.getNextOrderNumber(params);
  }

  get currentCompany$() :Observable<ICompany | null> {
    return this.clientStateService.currentCompany$;
  }

  get businessAccountsContacts$() :Observable<IBusinessAccountContact[]> {
    return this.clientStateService.businessAccountsContacts$;
  }

  get businessAccountShippingAddresses$() :Observable<any[]> {
    return this.clientStateService.businessAccountShippingAddresses$;
  }

  get pricingByRfq$() :Observable<{
    [key :string] :IPrice;
  }> {
    return this.pricingApiService.pricingByRfq$;
  }

  get currentSalesOrder$() :Observable<any> {
    return this.salesOrderStateService.currentSalesOrder$;
  }

  get currentCompany() :ICompany | null {
    return this.clientStateService.currentCompany;
  }
}
